import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle } from 'lucide-react';
import { extCompanyService } from '../../mantenimiento/services/extCompanyService.js';
import { ExtCompaniesTable } from './components/ExtCompaniesTable';
import { ExtCompanyDetails } from './components/ExtCompanyDetails';
import { ExtCompanyModal } from './components/ExtCompanyModal';
import { usePermissions } from '../../../hooks/usePermissions';
import { useFactory } from '../../../context/FactoryContext';


// Pagination Defaults
const DEFAULT_PAGE_SIZE = 5;
const PAGE_SIZE_OPTIONS = [5, 10];

const ConfigExternal = () => {
    const { hasAnyRoleKey, isLoading: isLoadingPermissions } = usePermissions();
    const { selectedFactory, factories } = useFactory();
    const queryClient = useQueryClient();

    // UI filters & pagination state
    const [searchQuery, setSearchQuery] = useState('');
    const [filterActive, setFilterActive] = useState('all');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    // View Handler
    const readOnlyMode = !hasAnyRoleKey('MAIN:Supervisor',
        'MAIN:Manager',
        'OT:Manager')

    // Selected company & modal state
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    const [companyToDelete, setCompanyToDelete] = useState(null);

    // Fetch all external companies (active and inactive, using pagination limit to 9999)
    //We should probably add a fetch more button as to not involve very big network queries.
    const {
        data: extCompaniesResponse,
        isLoading: isLoadingExtCompanies,
        isError: isErrorExtCompanies,
        error: extCompaniesError
    } = useQuery({
        queryKey: ['ext-companies'],
        queryFn: () => extCompanyService.getAllExtCompanies(false, 1, 9999),
        retry: 1,
    });

    const extCompanies = extCompaniesResponse?.data || [];


    // Get current factory from factory context
    const currentFactory = useMemo(() => {
        if (!selectedFactory || !factories) return null;
        return factories.find(
            (f) => (f.asset_code || f.assetCode || f.code || f.Code || f.name || '').toUpperCase() === selectedFactory.toUpperCase()
        ) || null;
    }, [selectedFactory, factories]);

    const currentFactoryId = currentFactory?.id || currentFactory?.ID;

    // Client-side search and filtering
    const filteredCompanies = useMemo(() => {
        let filtered = extCompanies;

        // Filter by selected factory from FactoryContext
        if (selectedFactory) {
            filtered = filtered.filter(company => {
                if (!Array.isArray(company?.factories) || company.factories.length === 0) {
                    return false;
                }
                return company.factories.some(f => {
                    if (typeof f === 'number' || typeof f === 'string') {
                        return (
                            (currentFactoryId !== undefined && currentFactoryId !== null && String(f) === String(currentFactoryId)) ||
                            String(f).toUpperCase() === selectedFactory.toUpperCase()
                        );
                    }
                    if (typeof f === 'object' && f !== null) {
                        const fId = f.id || f.ID;
                        const fCode = f.asset_code || f.assetCode || f.code || f.Code || f.name;
                        return (
                            (currentFactoryId !== undefined && currentFactoryId !== null && String(fId) === String(currentFactoryId)) ||
                            (fCode && String(fCode).toUpperCase() === selectedFactory.toUpperCase())
                        );
                    }
                    return false;
                });
            });
        }

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(company =>
                company.name?.toLowerCase().includes(query) ||
                company.tax_id?.toLowerCase().includes(query) ||
                company.contact_name?.toLowerCase().includes(query) ||
                company.email?.toLowerCase().includes(query) ||
                company.phone?.toLowerCase().includes(query) ||
                company.notes?.toLowerCase().includes(query)
            );
        }

        if (filterActive !== 'all') {
            const targetActive = filterActive === 'active';
            filtered = filtered.filter(company => company.active === targetActive);
        }

        // Sort alphabetically by company name (A-Z)
        return [...filtered].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

    }, [extCompanies, selectedFactory, currentFactoryId, searchQuery, filterActive]);

    // Find the selected company details (only if it matches current filter)
    const selectedCompany = useMemo(() => {
        return filteredCompanies.find(c => c.id === selectedCompanyId) || null;
    }, [selectedCompanyId, filteredCompanies]);

    // Client-side pagination logic
    const totalItems = filteredCompanies.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const paginatedCompanies = useMemo(
        () => filteredCompanies.slice((page - 1) * pageSize, page * pageSize),
        [filteredCompanies, page, pageSize]
    );
    const rangeStart = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
    const rangeEnd = Math.min(page * pageSize, totalItems);

    // Reset page to 1 when filters or page size change
    useEffect(() => {
        setPage(1);
    }, [selectedFactory, searchQuery, filterActive, pageSize]);

    // Clear selected company if it is no longer in the filtered list
    useEffect(() => {
        if (selectedCompanyId && !filteredCompanies.some(c => c.id === selectedCompanyId)) {
            setSelectedCompanyId(null);
        }
    }, [filteredCompanies, selectedCompanyId]);

    // Mutations
    const createMutation = useMutation({
        mutationFn: (companyData) => extCompanyService.createExtCompany(companyData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ext-companies'] });
            setIsModalOpen(false);
        },
        onError: (err) => {
            console.error('Error creating company:', err);
            throw err;
        }
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => extCompanyService.updateExtCompany(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ext-companies'] });
            setIsModalOpen(false);
            setEditingCompany(null);
        },
        onError: (err) => {
            console.error('Error updating company:', err);
            throw err;
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => extCompanyService.deleteExtCompany(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ext-companies'] });
            setCompanyToDelete(null);
            if (selectedCompanyId === companyToDelete) {
                setSelectedCompanyId(null);
            }
        },
        onError: (err) => {
            console.error('Error deleting company:', err);
            setCompanyToDelete(null);
            alert(err.message || 'Error deleting company');
        }
    });

    // Handlers
    const handleCreate = () => {
        setEditingCompany(null);
        setIsModalOpen(true);
    };

    const handleEdit = (company) => {
        setEditingCompany(company);
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
            setCompanyToDelete(id);
            deleteMutation.mutate(id);
        }
    };

    const handleSave = async (formData) => {
        if (editingCompany) {
            await updateMutation.mutateAsync({
                id: editingCompany.id,
                data: formData
            });
        } else {
            await createMutation.mutateAsync(formData);
        }
    };

    const handleCompanyClick = (company) => {
        setSelectedCompanyId(company.id);
    };

    // Loading/Error Screen
    if (isLoadingExtCompanies) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                    <p className="text-gray-400">Loading external companies...</p>
                </div>
            </div>
        );
    }

    if (isErrorExtCompanies) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-red-400 mb-2">Error loading companies</p>
                    <p className="text-gray-400 text-sm">{extCompaniesError?.message || 'Unknown error'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 w-full min-w-0 relative">
            <div className="mb-6">
                <h1 className="text-3xl pl-6 font-bold text-white mb-2">External Companies Configuration</h1>
                <p className="text-gray-400 pl-6">Manage external partner companies and their contact information.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-stretch">
                {/* Left Column: Companies Table */}
                <div className="min-w-0 flex flex-col min-h-0">
                    <ExtCompaniesTable
                        companies={paginatedCompanies}
                        totalItems={totalItems}
                        totalPages={totalPages}
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        rangeStart={rangeStart}
                        rangeEnd={rangeEnd}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        filterActive={filterActive}
                        setFilterActive={setFilterActive}
                        onCompanyClick={handleCompanyClick}
                        onCreate={handleCreate}
                        selectedCompanyId={selectedCompanyId}
                        readOnlyMode={readOnlyMode}
                    />
                </div>

                {/* Right Column: Company Details */}
                <div className="min-w-0 flex flex-col min-h-0 lg:sticky lg:top-6">
                    <ExtCompanyDetails
                        company={selectedCompany}
                        loading={isLoadingExtCompanies}
                        onDelete={handleDelete}
                        onEdit={handleEdit}
                        readOnlyMode={readOnlyMode}
                    />
                </div>
            </div>

            {/* Creation / Editing Modal */}
            {isModalOpen && (
                <ExtCompanyModal
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        setEditingCompany(null);
                    }}
                    onSave={handleSave}
                    company={editingCompany}
                />
            )}
        </div>
    );
};

export default ConfigExternal;
